import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  Alert,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import type { IContact, NewContactForm } from "../../types/venues.types";
import { SEARCH_MIN_CHARS } from "../utils/venue.utils";
import CustomTextField from "../../../../components/ui/Textfield";
import PhoneNumberField from "../../../../components/ui/PhoneNumberField";

interface AddContactModalProps {
  open: boolean;
  onClose: () => void;
  contactModalStep: "search" | "form";
  setContactModalStep: (step: "search" | "form") => void;
  contactSearch: string;
  setContactSearch: (value: string) => void;
  searchResults: IContact[];
  isSearching: boolean;
  savingContact: boolean;
  searchAttempted: boolean;
  newContactForm: NewContactForm;
  setNewContactForm: (form: NewContactForm) => void;
  onAddExistingContact: (contact: IContact) => void;
  onAddNewContact: () => void;
}

export const AddContactModal: React.FC<AddContactModalProps> = ({
  open,
  onClose,
  contactModalStep,
  setContactModalStep,
  contactSearch,
  setContactSearch,
  searchResults,
  isSearching,
  savingContact,
  searchAttempted,
  newContactForm,
  setNewContactForm,
  onAddExistingContact,
  onAddNewContact,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {contactModalStep === "search"
          ? "Add Contact to Venue"
          : "Create New Contact"}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {contactModalStep === "search" ? (
          <SearchContactStep
            contactSearch={contactSearch}
            setContactSearch={setContactSearch}
            searchResults={searchResults}
            isSearching={isSearching}
            savingContact={savingContact}
            searchAttempted={searchAttempted}
            onAddExistingContact={onAddExistingContact}
            setContactModalStep={setContactModalStep}
          />
        ) : (
          <NewContactFormStep
            newContactForm={newContactForm}
            setNewContactForm={setNewContactForm}
            savingContact={savingContact}
          />
        )}
      </DialogContent>
      <DialogActions>
        {contactModalStep === "form" && (
          <Button onClick={() => setContactModalStep("search")}>Back</Button>
        )}
        <Button onClick={onClose} disabled={savingContact}>
          Cancel
        </Button>
        {contactModalStep === "form" && (
          <Button
            onClick={onAddNewContact}
            variant="contained"
            disabled={
              !newContactForm.first_name.trim() ||
              !newContactForm.last_name.trim() ||
              !newContactForm.email.trim() ||
              savingContact
            }
          >
            {savingContact ? <CircularProgress size={20} /> : "Create & Add"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

interface SearchContactStepProps {
  contactSearch: string;
  setContactSearch: (value: string) => void;
  searchResults: IContact[];
  isSearching: boolean;
  savingContact: boolean;
  searchAttempted: boolean;
  onAddExistingContact: (contact: IContact) => void;
  setContactModalStep: (step: "search" | "form") => void;
}

const SearchContactStep: React.FC<SearchContactStepProps> = ({
  contactSearch,
  setContactSearch,
  searchResults,
  isSearching,
  savingContact,
  searchAttempted,
  onAddExistingContact,
  setContactModalStep,
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <CustomTextField
        fullWidth
        placeholder="Search existing contacts..."
        value={contactSearch}
        onChange={(e) => setContactSearch(e.target.value)}
        disabled={isSearching}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1 }} />,
        }}
        variant="outlined"
      />

      {contactSearch.length > 0 && (
        <Typography variant="caption" color="textSecondary">
          {contactSearch.length < SEARCH_MIN_CHARS
            ? `Type ${SEARCH_MIN_CHARS - contactSearch.length} more character${
                SEARCH_MIN_CHARS - contactSearch.length > 1 ? "s" : ""
              } to search`
            : "Searching..."}
        </Typography>
      )}

      {isSearching && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!isSearching &&
        searchAttempted &&
        contactSearch.trim().length >= SEARCH_MIN_CHARS && (
          <>
            {searchResults.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Found {searchResults.length} contact
                  {searchResults.length !== 1 ? "s" : ""}
                </Typography>
                <List sx={{ maxHeight: 300, overflow: "auto" }}>
                  {searchResults.map((contact) => (
                    <ListItemButton
                      key={contact.id}
                      onClick={() => onAddExistingContact(contact)}
                      disabled={savingContact}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <Typography variant="body2" fontWeight={600}>
                          {contact.first_name} {contact.last_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {contact.email}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ) : (
              <Alert severity="info">
                No contacts found matching &quot;{contactSearch}&quot;. Would
                you like to{" "}
                <Button
                  size="small"
                  onClick={() => setContactModalStep("form")}
                  sx={{ textTransform: "none" }}
                >
                  create a new contact?
                </Button>
              </Alert>
            )}
          </>
        )}

      {!searchAttempted && (
        <Alert severity="info">
          Type at least {SEARCH_MIN_CHARS} characters to search for existing
          contacts
        </Alert>
      )}
    </Box>
  );
};

interface NewContactFormStepProps {
  newContactForm: NewContactForm;
  setNewContactForm: (form: NewContactForm) => void;
  savingContact: boolean;
}

const NewContactFormStep: React.FC<NewContactFormStepProps> = ({
  newContactForm,
  setNewContactForm,
  savingContact,
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <CustomTextField
        fullWidth
        label="First Name"
        value={newContactForm.first_name}
        onChange={(e) =>
          setNewContactForm({
            ...newContactForm,
            first_name: e.target.value,
          })
        }
        disabled={savingContact}
      />
      <CustomTextField
        fullWidth
        label="Last Name"
        value={newContactForm.last_name}
        onChange={(e) =>
          setNewContactForm({
            ...newContactForm,
            last_name: e.target.value,
          })
        }
        disabled={savingContact}
      />
      <CustomTextField
        fullWidth
        label="Email"
        type="email"
        value={newContactForm.email}
        onChange={(e) =>
          setNewContactForm({
            ...newContactForm,
            email: e.target.value,
          })
        }
        disabled={savingContact}
      />
      <PhoneNumberField
        fullWidth
        defaultCountry="ZW"
        label="Phone Number"
        disabled={savingContact}
        value={newContactForm.phone ?? ""}
        onChange={(newPhoneNumber) =>
          setNewContactForm({
            ...newContactForm,
            phone: newPhoneNumber,
          })
        }
      />
    </Box>
  );
};

export default AddContactModal;
